// ABOUTME: The one create-business dialog, shared by the Agents tree's
// "+ New business" and Settings › Business. Submit goes
// through useMyBusinessStore.createBusiness, which auto-switches into the new
// business and preserves the half-done-create semantics below.

import { useCallback, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useMyBusinessStore } from '@/stores/useMyBusinessStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface NewBusinessDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (name: string) => Promise<void>
}

export function NewBusinessDialog({ open, onOpenChange, onSubmit }: NewBusinessDialogProps) {
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const error = useMyBusinessStore((s) => s.error)
  const pendingSwitchBusiness = useMyBusinessStore((s) => s.pendingSwitchBusiness)
  const clearBusinessError = useMyBusinessStore((s) => s.clearBusinessError)

  // Reopening the dialog must not show a stale error from an unrelated
  // action (e.g. a rename failure elsewhere on the page) — `error` is a
  // single shared field on the store, not scoped to create/switch.
  useEffect(() => {
    if (open) clearBusinessError()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) return
    setSubmitting(true)
    try {
      await onSubmit(name.trim())
      setName('')
      onOpenChange(false)
    } catch {
      // Surfaced below via the store's `error` / `pendingSwitchBusiness`.
    } finally {
      setSubmitting(false)
    }
  }, [name, onSubmit, onOpenChange])

  const handleOpenChange = useCallback(
    (v: boolean) => {
      if (!submitting) {
        setName('')
        clearBusinessError()
        onOpenChange(v)
      }
    },
    [submitting, onOpenChange, clearBusinessError]
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Business</DialogTitle>
          <DialogDescription>
            Create a new business profile. You can switch between businesses at any time.
          </DialogDescription>
        </DialogHeader>

        {pendingSwitchBusiness ? (
          // The business already exists — offering "Create Business" again
          // here would create a duplicate with the same name.
          <div
            className="border-warning/30 bg-warning/10 text-warning rounded-md border px-3 py-2 text-sm"
            role="alert"
            data-testid="business-switch-pending"
          >
            {error}
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="new-business-name">Business name</Label>
            <Input
              id="new-business-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My New Business"
              onKeyDown={(e) => e.key === 'Enter' && !submitting && handleSubmit()}
              data-testid="new-business-name-input"
            />
            {error && (
              <p className="text-destructive text-sm" role="alert" data-testid="new-business-error">
                {error}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {pendingSwitchBusiness ? (
            <Button
              onClick={() => handleOpenChange(false)}
              data-testid="business-switch-pending-close"
            >
              Got it
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !name.trim()}
                data-testid="create-business-submit"
              >
                {submitting && <Loader2 className="size-4 animate-spin" />}
                Create Business
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
